import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users as UsersIcon, Car, Route, Radio, Headphones,
  LogOut, Moon, Sun, Star, Send, ShieldCheck, DollarSign, Search,
  Clock, CalendarClock, Hourglass, Phone, Mail, MapPin, Ban, CheckCircle2,
  ChevronRight, User as UserIcon, Users2, Briefcase, CreditCard,
  Wallet, Megaphone, Percent, Banknote, Pencil, TrendingUp,
  FileText, Download, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import Avatar from "@/components/Avatar";
import Button from "@/components/Button";
import Modal from "@/components/Modal";
import { Spinner, EmptyState } from "@/components/States";
import StatusBadge from "@/components/StatusBadge";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { money, fmtDateTime, cn } from "@/lib/format";

const SECTIONS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "drivers", label: "Drivers", icon: Car },
  { key: "users", label: "Users", icon: UsersIcon },
  { key: "rides", label: "Rides", icon: Route },
  { key: "finance", label: "Finance", icon: Wallet },
  { key: "payouts", label: "Payouts", icon: Banknote },
  { key: "marketing", label: "Email", icon: Megaphone },
  { key: "live", label: "Live", icon: Radio },
  { key: "support", label: "Support", icon: Headphones },
];

const APPROVAL_ACTIONS = {
  pending: [["approved", "Approve", "primary"], ["declined", "Decline", "danger"]],
  approved: [["deactivated", "Revoke approval", "danger"]],
  declined: [["approved", "Approve", "primary"]],
  deactivated: [["approved", "Reactivate", "primary"]],
};

const rideGroup = (r) => {
  if (r.status === "searching") return "pending";
  if (r.status === "scheduled") {
    if (r.scheduled_ts && r.scheduled_ts * 1000 < Date.now()) return "past";
    return r.assigned_driver ? "upcoming" : "scheduled";
  }
  if (["accepted", "driver_enroute", "arrived", "in_progress"].includes(r.status)) return "live";
  if (r.status === "completed") return "completed";
  if (r.status === "cancelled") return "cancelled";
  return "other";
};

const RIDE_DELETABLE = (r) =>
  !(["accepted", "driver_enroute", "arrived", "in_progress"].includes(r.status) || r.status === "completed");

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <span className={cn("flex h-10 w-10 items-center justify-center rounded-xl", accent ? "bg-brand-600 text-white" : "bg-accent text-brand-600")}>
        <Icon className="h-5 w-5" />
      </span>
      <p className="mt-3 font-num text-3xl font-extrabold text-foreground">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { dark, toggle } = useTheme();
  const [section, setSection] = useState("overview");

  const logout = () => { signOut(); navigate("/auth", { replace: true }); };

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-border bg-card p-4 md:flex">
        <div className="mb-6 flex items-center justify-between px-2">
          <Logo size={30} />
        </div>
        <nav className="flex-1 space-y-1">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              data-testid={`admin-nav-${s.key}`}
              onClick={() => setSection(s.key)}
              className={cn("flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors",
                section === s.key ? "bg-accent text-brand-700 dark:text-brand-400" : "text-muted-foreground hover:bg-secondary")}
            >
              <s.icon className="h-5 w-5" /> {s.label}
            </button>
          ))}
        </nav>
        <div className="space-y-1 border-t border-border pt-3">
          <button onClick={toggle} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-muted-foreground hover:bg-secondary">
            {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />} {dark ? "Light mode" : "Dark mode"}
          </button>
          <button onClick={logout} data-testid="admin-logout" className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-destructive hover:bg-secondary">
            <LogOut className="h-5 w-5" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top tabs */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex items-center justify-between border-b border-border bg-card px-4 py-3 md:hidden">
          <Logo size={26} />
          <div className="flex gap-2">
            <button onClick={toggle} className="text-muted-foreground">{dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}</button>
            <button onClick={logout} className="text-destructive"><LogOut className="h-5 w-5" /></button>
          </div>
        </header>
        <div className="flex gap-1 overflow-x-auto border-b border-border bg-card px-2 py-2 no-scrollbar md:hidden">
          {SECTIONS.map((s) => (
            <button key={s.key} onClick={() => setSection(s.key)} className={cn("shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold", section === s.key ? "bg-accent text-brand-700 dark:text-brand-400" : "text-muted-foreground")}>
              {s.label}
            </button>
          ))}
        </div>

        <main className="flex-1 overflow-y-auto p-5">
          {section === "overview" && <Overview />}
          {section === "drivers" && <Drivers />}
          {section === "users" && <UsersList />}
          {section === "rides" && <Rides />}
          {section === "finance" && <Finance />}
          {section === "payouts" && <DriverPayouts />}
          {section === "marketing" && <Marketing />}
          {section === "live" && <Live />}
          {section === "support" && <Support />}
        </main>
      </div>
    </div>
  );
}

/* ---------------- shared pieces ---------------- */

function RideRow({ r, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`ride-row-${r.id}`}
      className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-4 text-left transition-colors hover:border-brand-400"
    >
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <StatusBadge status={r.status} />
          <span className="text-xs text-muted-foreground">
            {r.when === "scheduled" && r.scheduled_ts ? fmtDateTime(r.scheduled_ts) : fmtDateTime(r.created_at)}
          </span>
        </div>
        <p className="truncate text-sm text-foreground">{r.pickup?.label} → {r.destination?.label}</p>
        <p className="truncate text-xs text-muted-foreground">
          {r.customer_name}{r.assigned_driver ? ` · ${r.assigned_driver.name}` : ""}
        </p>
      </div>
      <span className="font-num font-bold text-foreground">{money(r.final_fare || r.recommended_fare)}</span>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
    </button>
  );
}

function DRow({ icon: Icon, label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="break-words text-sm font-semibold text-foreground">{value}</p>
      </div>
    </div>
  );
}

function RideDetailModal({ ride, onClose, onChanged }) {
  const [deleting, setDeleting] = useState(false);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { setConfirm(false); }, [ride]);
  if (!ride) return null;
  const r = ride;
  const canDelete = RIDE_DELETABLE(r);
  const del = async () => {
    setDeleting(true);
    try {
      await api(`/admin/rides/${r.id}`, { method: "DELETE" });
      toast.success("Request deleted");
      onChanged?.();
      onClose?.();
    } catch (e) { toast.error(e?.message || "Failed to delete"); }
    finally { setDeleting(false); }
  };
  return (
    <Modal open={!!ride} onClose={onClose} title="Ride detail" subtitle={r.id} size="xl">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <StatusBadge status={r.status} />
        <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-semibold text-muted-foreground">
          {r.when === "scheduled" ? "Scheduled" : "On-demand"}
        </span>
        {r.required_class_label && (
          <span className="rounded-full bg-accent px-2.5 py-1 text-xs font-semibold text-brand-700 dark:text-brand-400">{r.required_class_label}</span>
        )}
      </div>
      <div className="grid gap-x-6 sm:grid-cols-2">
        <DRow icon={MapPin} label="Pickup" value={r.pickup?.label} />
        <DRow icon={MapPin} label="Destination" value={r.destination?.label} />
        <DRow icon={UserIcon} label="Customer" value={r.customer_name} />
        <DRow icon={Car} label="Driver" value={r.assigned_driver?.name || "—"} />
        <DRow icon={CalendarClock} label={r.when === "scheduled" ? "Scheduled for" : "Requested"} value={r.when === "scheduled" && r.scheduled_ts ? fmtDateTime(r.scheduled_ts) : fmtDateTime(r.created_at)} />
        <DRow icon={Users2} label="Passengers" value={r.passengers} />
        <DRow icon={Briefcase} label="Bags" value={r.bags} />
        <DRow icon={Route} label="Distance" value={r.distance_mi ? `${Number(r.distance_mi).toFixed(1)} mi` : null} />
        <DRow icon={DollarSign} label="Recommended fare" value={money(r.recommended_fare)} />
        <DRow icon={DollarSign} label="Final fare" value={r.final_fare ? money(r.final_fare) : "—"} />
        <DRow icon={DollarSign} label="Tip" value={r.tip ? money(r.tip) : "—"} />
        <DRow icon={CreditCard} label="Payment" value={r.payment_status || "—"} />
      </div>
      {r.airport_info && (r.airport_info.airline || r.airport_info.flight) && (
        <div className="mt-2 rounded-xl bg-secondary p-3">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Airport info</p>
          <p className="text-sm font-semibold text-foreground">
            {[r.airport_info.airline, r.airport_info.flight].filter(Boolean).join(" · ")}
          </p>
        </div>
      )}
      {canDelete && (
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-4">
          {!confirm ? (
            <Button size="sm" variant="danger" onClick={() => setConfirm(true)} data-testid="ride-delete">
              <Ban className="h-4 w-4" /> Delete request
            </Button>
          ) : (
            <>
              <span className="text-sm font-medium text-foreground">Delete this request permanently?</span>
              <Button size="sm" variant="danger" onClick={del} loading={deleting} data-testid="ride-delete-confirm">Yes, delete</Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirm(false)} data-testid="ride-delete-cancel">Cancel</Button>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

function DocTile({ label, src }) {
  const isImage = typeof src === "string" && (src.startsWith("data:image") || /\.(png|jpe?g|webp|gif)$/i.test(src) || src.includes("images."));
  const ext = typeof src === "string" && src.startsWith("data:") ? (src.slice(5).split(";")[0].split("/")[1] || "file") : "file";
  const download = `${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.${isImage ? (ext === "jpeg" ? "jpg" : ext) : (ext || "pdf")}`;
  return (
    <div className="flex flex-col" data-testid={`doc-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <span className="mb-1.5 text-xs font-medium text-muted-foreground">{label}</span>
      {!src ? (
        <div className="flex h-28 items-center justify-center rounded-xl border border-dashed border-border bg-secondary text-xs text-muted-foreground">
          Not provided
        </div>
      ) : (
        <div className="group relative overflow-hidden rounded-xl border border-border bg-secondary">
          <a href={src} target="_blank" rel="noreferrer" className="block h-28 w-full" title="Open full size">
            {isImage ? (
              <img src={src} alt={label} className="h-28 w-full object-cover" />
            ) : (
              <span className="flex h-28 w-full flex-col items-center justify-center gap-1 text-brand-600">
                <FileText className="h-8 w-8" />
                <span className="text-[11px] font-semibold uppercase">{ext}</span>
              </span>
            )}
          </a>
          <a href={src} download={download} data-testid={`download-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="absolute bottom-1.5 right-1.5 flex items-center gap-1 rounded-lg bg-black/70 px-2 py-1 text-[11px] font-semibold text-white opacity-90 transition-opacity hover:opacity-100">
            <Download className="h-3.5 w-3.5" /> Download
          </a>
        </div>
      )}
    </div>
  );
}

function PersonDetailModal({ person, onClose, onChanged }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [vehicleImg, setVehicleImg] = useState(null);
  useEffect(() => {
    setConfirmDel(false);
    setVehicleImg(null);
    if (person) setForm({
      name: person.name || "", phone: person.phone || "", vehicle: person.vehicle || "",
      plate: person.plate || "", color: person.color || "", rating: person.rating ?? "",
    });
    const desc = person?.vehicle || [person?.vehicle_year, person?.vehicle_make, person?.vehicle_model].filter(Boolean).join(" ");
    if (person?.role === "driver" && desc) {
      api(`/vehicle-image?desc=${encodeURIComponent(desc)}`).then((r) => setVehicleImg(r?.image || null)).catch(() => {});
    }
  }, [person]);
  if (!person) return null;
  const u = person;
  const isDriver = u.role === "driver";

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        name: form.name, phone: form.phone,
        ...(isDriver ? { vehicle: form.vehicle, plate: form.plate, color: form.color } : {}),
        rating: form.rating === "" ? undefined : Number(form.rating),
      };
      await api(`/admin/users/${u.id}`, { method: "PATCH", body });
      toast.success("Saved");
      onChanged?.();
    } catch (e) { toast.error(e?.message || "Failed"); }
    finally { setSaving(false); }
  };

  const setApproval = async (status) => {
    try {
      await api(`/admin/drivers/${u.id}/status`, { method: "POST", body: { status } });
      toast.success(`Driver ${status}`);
      onChanged?.();
    } catch (e) { toast.error(e?.message || "Failed"); }
  };

  const setActive = async (active) => {
    try {
      await api(`/admin/users/${u.id}/active`, { method: "POST", body: { active } });
      toast.success(active ? "Account activated" : "Account deactivated");
      onChanged?.();
    } catch (e) { toast.error(e?.message || "Failed"); }
  };

  const del = async () => {
    setDeleting(true);
    try {
      await api(`/admin/users/${u.id}`, { method: "DELETE" });
      toast.success(`${isDriver ? "Driver" : "Customer"} deleted`);
      onChanged?.();
    } catch (e) { toast.error(e?.message || "Failed to delete"); }
    finally { setDeleting(false); }
  };

  const field = "h-11 w-full rounded-xl border border-border bg-secondary px-3 text-sm text-foreground outline-none focus:border-brand-500";

  return (
    <Modal open={!!person} onClose={onClose} title={u.name} subtitle={u.email} size="xl">
      <div className="mb-4 flex items-center gap-3">
        <Avatar src={u.photo} name={u.name} size={56} />
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-semibold uppercase text-muted-foreground">{u.role}</span>
          {isDriver && <StatusBadge status={u.approval_status} />}
          {u.disabled
            ? <span className="rounded-full bg-destructive/15 px-2.5 py-1 text-xs font-semibold text-destructive">Deactivated</span>
            : <span className="rounded-full bg-success/15 px-2.5 py-1 text-xs font-semibold text-success">Active</span>}
          {isDriver && u.online && <span className="rounded-full bg-success/15 px-2.5 py-1 text-xs font-semibold text-success">Online</span>}
        </div>
      </div>

      <div className="grid gap-x-6 sm:grid-cols-2">
        <DRow icon={Mail} label="Email" value={u.email} />
        <DRow icon={Phone} label="Phone" value={u.phone || "—"} />
        <DRow icon={Star} label="Rating" value={Number(u.rating || 5).toFixed(1)} />
        <DRow icon={CalendarClock} label="Joined" value={u.created_at ? fmtDateTime(u.created_at) : "—"} />
        {isDriver && <>
          <DRow icon={Car} label="Vehicle" value={u.vehicle || [u.vehicle_year, u.vehicle_make, u.vehicle_model].filter(Boolean).join(" ") || "—"} />
          <DRow icon={Car} label="Plate" value={u.plate || "—"} />
          <DRow icon={Car} label="Color" value={u.color || "—"} />
          <DRow icon={ShieldCheck} label="Vehicle class" value={u.vehicle_class_label || "—"} />
          <DRow icon={CreditCard} label="License #" value={u.license_number || "—"} />
        </>}
      </div>

      {/* Documents & photos (drivers) */}
      {isDriver && (
        <div className="mt-4 rounded-2xl border border-border p-4" data-testid="driver-documents">
          <p className="mb-3 flex items-center gap-2 text-sm font-bold text-foreground"><ImageIcon className="h-4 w-4" /> Documents & photos</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <DocTile label="Profile photo" src={u.photo} />
            <DocTile label="Vehicle" src={vehicleImg} />
            <DocTile label="Driver's license" src={u.license_doc} />
            <DocTile label="Insurance" src={u.insurance_doc} />
            <DocTile label="Registration" src={u.registration_doc} />
          </div>
        </div>
      )}

      {/* Edit */}
      <div className="mt-4 rounded-2xl border border-border p-4">
        <p className="mb-3 text-sm font-bold text-foreground">Edit details</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block"><span className="mb-1 block text-xs text-muted-foreground">Name</span>
            <input value={form.name ?? ""} onChange={set("name")} className={field} data-testid="edit-name" /></label>
          <label className="block"><span className="mb-1 block text-xs text-muted-foreground">Phone</span>
            <input value={form.phone ?? ""} onChange={set("phone")} className={field} data-testid="edit-phone" /></label>
          <label className="block"><span className="mb-1 block text-xs text-muted-foreground">Rating</span>
            <input value={form.rating ?? ""} onChange={set("rating")} type="number" step="0.1" min="0" max="5" className={field} data-testid="edit-rating" /></label>
          {isDriver && <>
            <label className="block"><span className="mb-1 block text-xs text-muted-foreground">Vehicle</span>
              <input value={form.vehicle ?? ""} onChange={set("vehicle")} className={field} data-testid="edit-vehicle" /></label>
            <label className="block"><span className="mb-1 block text-xs text-muted-foreground">Plate</span>
              <input value={form.plate ?? ""} onChange={set("plate")} className={field} data-testid="edit-plate" /></label>
            <label className="block"><span className="mb-1 block text-xs text-muted-foreground">Color</span>
              <input value={form.color ?? ""} onChange={set("color")} className={field} data-testid="edit-color" /></label>
          </>}
        </div>
        <Button size="sm" className="mt-3" onClick={save} loading={saving} data-testid="save-person">Save changes</Button>
      </div>

      {/* Actions */}
      <div className="mt-4 rounded-2xl border border-border p-4">
        <p className="mb-3 text-sm font-bold text-foreground">Account actions</p>
        <div className="flex flex-wrap gap-2">
          {isDriver && (APPROVAL_ACTIONS[u.approval_status] || []).map(([status, label, variant]) => (
            <Button key={status} size="sm" variant={variant} onClick={() => setApproval(status)} data-testid={`person-${status}`}>{label}</Button>
          ))}
          {u.disabled
            ? <Button size="sm" variant="primary" onClick={() => setActive(true)} data-testid="person-activate"><CheckCircle2 className="h-4 w-4" /> Activate account</Button>
            : <Button size="sm" variant="secondary" onClick={() => setActive(false)} data-testid="person-deactivate"><Ban className="h-4 w-4" /> Deactivate account</Button>}
        </div>

        {/* Danger zone: permanent delete */}
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-4">
          {!confirmDel ? (
            <Button size="sm" variant="danger" onClick={() => setConfirmDel(true)} data-testid="person-delete">
              <Ban className="h-4 w-4" /> Delete {isDriver ? "driver" : "customer"}
            </Button>
          ) : (
            <>
              <span className="text-sm font-medium text-foreground">Permanently delete this account? Completed ride history is kept.</span>
              <Button size="sm" variant="danger" onClick={del} loading={deleting} data-testid="person-delete-confirm">Yes, delete</Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmDel(false)} data-testid="person-delete-cancel">Cancel</Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

function PersonRow({ u, onClick }) {
  return (
    <button onClick={onClick} data-testid={`person-row-${u.id}`}
      className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-4 text-left transition-colors hover:border-brand-400">
      <Avatar src={u.photo} name={u.name} size={44} />
      <div className="min-w-0 flex-1">
        <p className="truncate font-bold text-foreground">{u.name}</p>
        <p className="truncate text-sm text-muted-foreground">{u.email}{u.vehicle ? ` · ${u.vehicle}` : ""}{u.plate ? ` · ${u.plate}` : ""}</p>
      </div>
      {u.disabled && <span className="rounded-full bg-destructive/15 px-2 py-0.5 text-[10px] font-semibold text-destructive">Off</span>}
      {u.role === "driver" && <StatusBadge status={u.approval_status} />}
      <span className="flex items-center gap-1 text-sm text-muted-foreground"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {Number(u.rating || 5).toFixed(1)}</span>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
    </button>
  );
}

/* ---------------- Overview ---------------- */

function OverviewColumn({ icon: Icon, title, count, rides, onOpen, tint }) {
  return (
    <div className="flex flex-col rounded-2xl border border-border bg-card">
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <span className={cn("flex h-8 w-8 items-center justify-center rounded-lg", tint)}><Icon className="h-4 w-4" /></span>
        <p className="text-sm font-bold text-foreground">{title}</p>
        <span className="ml-auto rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold text-muted-foreground">{count}</span>
      </div>
      <div className="max-h-[440px] space-y-2 overflow-y-auto p-3">
        {rides.length === 0
          ? <p className="px-2 py-6 text-center text-sm text-muted-foreground">Nothing here</p>
          : rides.map((r) => <RideRow key={r.id} r={r} onClick={() => onOpen(r)} />)}
      </div>
    </div>
  );
}

function Overview() {
  const [o, setO] = useState(null);
  const [rides, setRides] = useState([]);
  const [detail, setDetail] = useState(null);
  const loadRides = () => api("/admin/rides").then((r) => setRides(r.rides || [])).catch(() => setRides([]));
  useEffect(() => {
    api("/admin/overview").then(setO).catch(() => {});
    loadRides();
  }, []);
  const groups = useMemo(() => {
    const g = { upcoming: [], live: [], pending: [] };
    for (const r of rides) {
      const grp = rideGroup(r);
      if (grp === "upcoming" || grp === "scheduled") g.upcoming.push(r);
      else if (grp === "live") g.live.push(r);
      else if (grp === "pending") g.pending.push(r);
    }
    return g;
  }, [rides]);
  if (!o) return <Spinner className="h-7 w-7 text-brand-600" />;
  return (
    <div>
      <h1 className="mb-5 text-2xl font-extrabold text-foreground">Overview</h1>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard icon={DollarSign} label="Revenue" value={money(o.revenue)} accent />
        <StatCard icon={Route} label="Total rides" value={o.total_rides} />
        <StatCard icon={Radio} label="Active rides" value={o.active_rides} />
        <StatCard icon={ShieldCheck} label="Completed" value={o.completed_trips} />
        <StatCard icon={Car} label="Drivers" value={o.drivers} />
        <StatCard icon={Car} label="Drivers online" value={o.drivers_online} />
        <StatCard icon={UsersIcon} label="Customers" value={o.customers} />
        <StatCard icon={DollarSign} label="Tips" value={money(o.tips)} />
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <OverviewColumn icon={CalendarClock} title="Upcoming rides" tint="bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400" count={groups.upcoming.length} rides={groups.upcoming} onOpen={setDetail} />
        <OverviewColumn icon={Radio} title="Current live rides" tint="bg-success/15 text-success" count={groups.live.length} rides={groups.live} onOpen={setDetail} />
        <OverviewColumn icon={Hourglass} title="Pending requests" tint="bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400" count={groups.pending.length} rides={groups.pending} onOpen={setDetail} />
      </div>
      <RideDetailModal ride={detail} onClose={() => setDetail(null)} onChanged={() => { loadRides(); api("/admin/overview").then(setO).catch(() => {}); }} />
    </div>
  );
}

/* ---------------- Drivers ---------------- */

function Drivers() {
  const [users, setUsers] = useState(null);
  const [active, setActive] = useState(null);
  const load = () => api("/admin/users").then((r) => setUsers((r.users || []).filter((u) => u.role === "driver"))).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);
  const onChanged = () => { load(); setActive(null); };
  if (!users) return <Spinner className="h-7 w-7 text-brand-600" />;
  const pending = users.filter((u) => u.approval_status === "pending");
  const approved = users.filter((u) => u.approval_status === "approved");
  const others = users.filter((u) => !["pending", "approved"].includes(u.approval_status));
  return (
    <div>
      <h1 className="mb-5 text-2xl font-extrabold text-foreground">Drivers</h1>
      <Group title="Pending applications" count={pending.length}>
        {pending.length === 0 ? <EmptyState icon={Hourglass} title="No pending applications" /> :
          pending.map((u) => <PersonRow key={u.id} u={u} onClick={() => setActive(u)} />)}
      </Group>
      <Group title="Approved drivers" count={approved.length}>
        {approved.length === 0 ? <EmptyState icon={Car} title="No approved drivers" /> :
          approved.map((u) => <PersonRow key={u.id} u={u} onClick={() => setActive(u)} />)}
      </Group>
      {others.length > 0 && (
        <Group title="Declined / deactivated" count={others.length}>
          {others.map((u) => <PersonRow key={u.id} u={u} onClick={() => setActive(u)} />)}
        </Group>
      )}
      <PersonDetailModal person={active} onClose={() => setActive(null)} onChanged={onChanged} />
    </div>
  );
}

function Group({ title, count, children }) {
  return (
    <section className="mb-6">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-muted-foreground">
        {title} <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">{count}</span>
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

/* ---------------- Users ---------------- */

function UsersList() {
  const [users, setUsers] = useState(null);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api("/admin/users").then((r) => setUsers((r.users || []).filter((u) => u.role === "customer"))).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);
  const onChanged = () => { load(); setActive(null); };
  if (!users) return <Spinner className="h-7 w-7 text-brand-600" />;
  const filtered = users.filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase()));
  const activeUsers = filtered.filter((u) => !u.disabled);
  const banned = filtered.filter((u) => u.disabled);
  return (
    <div>
      <h1 className="mb-5 text-2xl font-extrabold text-foreground">Users</h1>
      <div className="mb-5 flex items-center gap-2 rounded-xl border border-border bg-card px-3">
        <Search className="h-4 w-4 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search riders…" data-testid="users-search" className="h-11 w-full bg-transparent text-sm text-foreground outline-none" />
      </div>
      <Group title="Active riders" count={activeUsers.length}>
        {activeUsers.length === 0 ? <EmptyState icon={UsersIcon} title="No riders" /> :
          activeUsers.map((u) => <PersonRow key={u.id} u={u} onClick={() => setActive(u)} />)}
      </Group>
      {banned.length > 0 && (
        <Group title="Deactivated riders" count={banned.length}>
          {banned.map((u) => <PersonRow key={u.id} u={u} onClick={() => setActive(u)} />)}
        </Group>
      )}
      <PersonDetailModal person={active} onClose={() => setActive(null)} onChanged={onChanged} />
    </div>
  );
}

/* ---------------- Rides ---------------- */

const RIDE_TABS = [
  { key: "upcoming", label: "Upcoming", icon: CalendarClock },
  { key: "pending", label: "Pending", icon: Hourglass },
  { key: "scheduled", label: "Scheduled", icon: Clock },
  { key: "past", label: "Past/expired", icon: Clock },
  { key: "completed", label: "Completed", icon: CheckCircle2 },
  { key: "cancelled", label: "Cancelled", icon: Ban },
];

function Rides() {
  const [rides, setRides] = useState(null);
  const [tab, setTab] = useState("upcoming");
  const [detail, setDetail] = useState(null);
  const load = () => api("/admin/rides").then((r) => setRides(r.rides || [])).catch(() => setRides([]));
  useEffect(() => { load(); }, []);
  const groups = useMemo(() => {
    const g = { upcoming: [], pending: [], scheduled: [], past: [], completed: [], cancelled: [], live: [], other: [] };
    for (const r of rides || []) g[rideGroup(r)]?.push(r);
    return g;
  }, [rides]);
  if (!rides) return <Spinner className="h-7 w-7 text-brand-600" />;
  const list = groups[tab] || [];
  return (
    <div>
      <h1 className="mb-5 text-2xl font-extrabold text-foreground">Rides</h1>
      <div className="mb-4 flex gap-1 overflow-x-auto no-scrollbar">
        {RIDE_TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`rides-tab-${t.key}`}
            className={cn("flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors",
              tab === t.key ? "bg-brand-600 text-white" : "bg-card text-muted-foreground hover:bg-secondary border border-border")}>
            <t.icon className="h-4 w-4" /> {t.label}
            <span className={cn("rounded-full px-1.5 text-xs", tab === t.key ? "bg-white/20" : "bg-secondary")}>{(groups[t.key] || []).length}</span>
          </button>
        ))}
      </div>
      {list.length === 0 ? <EmptyState icon={Route} title="No rides here" /> : (
        <div className="space-y-2">
          {list.map((r) => <RideRow key={r.id} r={r} onClick={() => setDetail(r)} />)}
        </div>
      )}
      <RideDetailModal ride={detail} onClose={() => setDetail(null)} onChanged={load} />
    </div>
  );
}

/* ---------------- Finance ---------------- */

const PCT = (r) => `${Math.round((r || 0) * 100)}%`;

function CommissionEditor({ rate, onSaved }) {
  const [val, setVal] = useState(Math.round((rate || 0) * 100));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(Math.round((rate || 0) * 100)); }, [rate]);
  const save = async () => {
    setSaving(true);
    try {
      await api("/admin/settings", { method: "PATCH", body: { commission_rate: Number(val) / 100 } });
      toast.success("Commission updated");
      onSaved?.();
    } catch (e) { toast.error(e?.message || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card p-4">
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-brand-600"><Percent className="h-5 w-5" /></span>
      <div className="flex-1">
        <p className="text-sm font-bold text-foreground">Platform commission</p>
        <p className="text-xs text-muted-foreground">Taken from each ride fare (tips excluded). Currently {PCT(rate)}.</p>
      </div>
      <div className="flex items-center gap-2">
        <input type="number" min="0" max="90" value={val} onChange={(e) => setVal(e.target.value)} data-testid="commission-input"
          className="h-11 w-20 rounded-xl border border-border bg-secondary px-3 text-sm text-foreground outline-none focus:border-brand-500" />
        <span className="text-sm text-muted-foreground">%</span>
        <Button size="sm" onClick={save} loading={saving} data-testid="commission-save">Save</Button>
      </div>
    </div>
  );
}

const PAYOUT_STATUS = {
  PENDING: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
  PROCESSING: "bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-400",
  PAID: "bg-success/15 text-success",
  FAILED: "bg-destructive/15 text-destructive",
  CANCELED: "bg-secondary text-muted-foreground",
};

function Finance() {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("payouts");
  const load = () => api("/admin/finance").then(setD).catch(() => setD(null));
  useEffect(() => { load(); }, []);
  if (!d) return <Spinner className="h-7 w-7 text-brand-600" />;
  const s = d.summary;
  return (
    <div>
      <h1 className="mb-5 text-2xl font-extrabold text-foreground">Finance</h1>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard icon={DollarSign} label={`Platform revenue (${PCT(d.commission_rate)})`} value={money(s.platform_revenue)} accent />
        <StatCard icon={TrendingUp} label="Gross fares" value={money(s.gross_fares)} />
        <StatCard icon={DollarSign} label="Tips" value={money(s.tips)} />
        <StatCard icon={Car} label="Driver earnings" value={money(s.driver_earnings)} />
        <StatCard icon={Banknote} label="Paid out" value={money(s.paid_out)} />
        <StatCard icon={Hourglass} label="Pending payouts" value={money(s.pending_payouts)} />
        <StatCard icon={Wallet} label="Outstanding owed" value={money(s.outstanding)} />
        <StatCard icon={CreditCard} label="Stripe collected" value={money(s.stripe_paid_total)} />
      </div>

      <div className="mt-5"><CommissionEditor rate={d.commission_rate} onSaved={load} /></div>

      <div className="mb-4 mt-6 flex gap-1 overflow-x-auto no-scrollbar">
        {[["payouts", "Recent payouts", Banknote], ["balances", "Driver balances", Wallet], ["payments", "Payments", CreditCard]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`finance-tab-${k}`}
            className={cn("flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors",
              tab === k ? "bg-brand-600 text-white" : "bg-card text-muted-foreground hover:bg-secondary border border-border")}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "payouts" && (
        d.payouts.length === 0 ? <EmptyState icon={Banknote} title="No payouts yet" subtitle="Automatic daily ACH payouts to drivers appear here." /> : (
          <div className="space-y-2">
            {d.payouts.map((p) => (
              <div key={p.id} data-testid={`payout-${p.id}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card p-4">
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-foreground">{p.driver_name} · <span className="font-num">{money(p.amount)}</span></p>
                  <p className="truncate text-xs text-muted-foreground">{p.payout_type || "ACH"} · {fmtDateTime(p.requested_at)}{p.failure_reason ? ` · ${p.failure_reason}` : ""}</p>
                </div>
                <span className={cn("rounded-full px-2.5 py-1 text-xs font-semibold", PAYOUT_STATUS[p.status] || "bg-secondary text-muted-foreground")}>{(p.status || "").toLowerCase()}</span>
              </div>
            ))}
          </div>
        )
      )}

      {tab === "balances" && (
        d.drivers.length === 0 ? <EmptyState icon={Wallet} title="No driver earnings yet" /> : (
          <div className="overflow-x-auto rounded-2xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Driver</th><th className="px-3 py-3">Trips</th>
                  <th className="px-3 py-3">Gross</th><th className="px-3 py-3">Commission</th>
                  <th className="px-3 py-3">Net</th><th className="px-3 py-3">Paid</th>
                  <th className="px-3 py-3">Pending</th><th className="px-4 py-3">Available</th>
                </tr>
              </thead>
              <tbody>
                {d.drivers.map((dr) => (
                  <tr key={dr.driver_id} data-testid={`balance-${dr.driver_id}`} className="border-t border-border bg-card">
                    <td className="px-4 py-3 font-semibold text-foreground">{dr.name}</td>
                    <td className="px-3 py-3 text-muted-foreground">{dr.trips}</td>
                    <td className="px-3 py-3 font-num">{money(dr.gross)}</td>
                    <td className="px-3 py-3 font-num text-destructive">−{money(dr.commission)}</td>
                    <td className="px-3 py-3 font-num">{money(dr.net)}</td>
                    <td className="px-3 py-3 font-num text-success">{money(dr.paid)}</td>
                    <td className="px-3 py-3 font-num text-amber-600">{money(dr.pending)}</td>
                    <td className="px-4 py-3 font-num font-bold text-foreground">{money(dr.available)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === "payments" && (
        d.payments.length === 0 ? <EmptyState icon={CreditCard} title="No payments yet" /> : (
          <div className="space-y-2">
            {d.payments.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card p-4">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-foreground">{p.customer_name}{p.driver_name ? ` → ${p.driver_name}` : ""}</p>
                  <p className="text-xs text-muted-foreground">{fmtDateTime(p.at)} · fare {money(p.fare)}{p.tip > 0 ? ` · tip ${money(p.tip)}` : ""}</p>
                </div>
                <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-semibold text-muted-foreground">{p.payment_status}</span>
                <div className="text-right">
                  <p className="font-num font-bold text-foreground">{money(p.total)}</p>
                  <p className="text-xs text-muted-foreground">comm {money(p.commission)} · net {money(p.driver_net)}</p>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

/* ---------------- Driver Payouts ---------------- */

const PAYOUT_FILTERS = [["", "All"], ["PROCESSING", "Processing"], ["PAID", "Paid"], ["FAILED", "Failed"]];

function DriverPayouts() {
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState("");
  const [running, setRunning] = useState(false);
  const load = (f = filter) => api(`/admin/payouts${f ? `?status=${f}` : ""}`).then(setD).catch(() => setD({ payouts: [], counts: {} }));
  useEffect(() => { load(filter); }, [filter]);

  const runDaily = async () => {
    setRunning(true);
    try {
      const r = await api("/admin/payouts/run-daily", { method: "POST" });
      if (r.ok === false) toast.error(r.reason || "Payouts disabled");
      else toast.success(`Daily payout run complete — ${r.processed} processed`);
      load(filter);
    } catch (e) { toast.error(e?.message || "Failed to run payouts"); }
    finally { setRunning(false); }
  };

  if (!d) return <Spinner className="h-7 w-7 text-brand-600" />;
  const rows = d.payouts || [];

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-extrabold text-foreground">Driver payouts</h1>
        <Button onClick={runDaily} loading={running} data-testid="run-daily-payouts">Run daily payouts</Button>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">Automatic once-per-day ACH bank payouts via Stripe Connect. Minimum {money(d.minimum || 10)}.</p>

      <div className="mb-4 flex gap-1 overflow-x-auto no-scrollbar">
        {PAYOUT_FILTERS.map(([k, label]) => (
          <button key={k || "all"} onClick={() => setFilter(k)} data-testid={`payout-filter-${(k || "all").toLowerCase()}`}
            className={cn("flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors",
              filter === k ? "bg-brand-600 text-white" : "bg-card text-muted-foreground hover:bg-secondary border border-border")}>
            {label}{d.counts?.[k] ? <span className="rounded-full bg-white/20 px-1.5 text-xs">{d.counts[k]}</span> : null}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={Banknote} title="No payouts yet" subtitle="Approved drivers with connected bank accounts get paid automatically each day." />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Driver</th><th className="px-3 py-3">Amount</th>
                <th className="px-3 py-3">Payout date</th><th className="px-3 py-3">Status</th>
                <th className="px-4 py-3">Stripe payout ID</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} data-testid={`admin-payout-${p.id}`} className="border-t border-border bg-card">
                  <td className="px-4 py-3 font-semibold text-foreground">{p.driver_name || "Driver"}</td>
                  <td className="px-3 py-3 font-num font-bold text-foreground">{money(p.amount)}</td>
                  <td className="px-3 py-3 text-muted-foreground">{fmtDateTime(p.paid_at || p.requested_at)}</td>
                  <td className="px-3 py-3">
                    <span className={cn("rounded-full px-2.5 py-1 text-xs font-semibold", PAYOUT_STATUS[p.status] || "bg-secondary text-muted-foreground")}>{(p.status || "").toLowerCase()}</span>
                    {p.failure_reason ? <p className="mt-1 text-xs text-destructive">{p.failure_reason}</p> : null}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{p.stripe_payout_id || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------- Email Marketing ---------------- */

const SEGMENTS = [["all", "All users"], ["riders", "Riders only"], ["drivers", "Drivers only"]];

function Marketing() {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [segment, setSegment] = useState("all");
  const [sending, setSending] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api("/admin/campaigns").then((r) => setCampaigns(r.campaigns || [])).catch(() => {});
    api("/admin/settings").then((r) => setNotifs(r.notifications || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Subject and message are required"); return; }
    setSending(true);
    try {
      const r = await api("/admin/campaigns", { method: "POST", body: { subject, body, segment } });
      toast.success(`Sent to ${r.sent} recipient${r.sent === 1 ? "" : "s"}`);
      setSubject(""); setBody("");
      load();
    } catch (e) { toast.error(e?.message || "Failed to send"); }
    finally { setSending(false); }
  };

  const toggleNotif = async (event, enabled) => {
    try {
      await api(`/admin/notifications/${event}`, { method: "PUT", body: { enabled } });
      setNotifs((ns) => ns.map((n) => n.event === event ? { ...n, enabled } : n));
    } catch (e) { toast.error(e?.message || "Failed"); }
  };

  const field = "h-11 w-full rounded-xl border border-border bg-secondary px-3 text-sm text-foreground outline-none focus:border-brand-500";

  return (
    <div>
      <h1 className="mb-5 text-2xl font-extrabold text-foreground">Email marketing</h1>

      <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
        {/* Compose */}
        <div className="rounded-2xl border border-border bg-card p-5">
          <p className="mb-3 flex items-center gap-2 text-sm font-bold text-foreground"><Megaphone className="h-4 w-4 text-brand-600" /> Compose campaign</p>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {SEGMENTS.map(([k, label]) => (
                <button key={k} onClick={() => setSegment(k)} data-testid={`segment-${k}`}
                  className={cn("rounded-full px-3.5 py-2 text-sm font-semibold transition-colors",
                    segment === k ? "bg-brand-600 text-white" : "bg-secondary text-muted-foreground hover:bg-muted")}>
                  {label}
                </button>
              ))}
            </div>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject line" data-testid="campaign-subject" className={field} />
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} data-testid="campaign-body"
              placeholder="Write your message… Use {name} to personalize. HTML and line breaks supported."
              className="w-full rounded-xl border border-border bg-secondary p-3 text-sm text-foreground outline-none focus:border-brand-500" />
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">Tip: <code className="rounded bg-secondary px-1">{"{name}"}</code> inserts the recipient&apos;s name.</p>
              <Button size="sm" onClick={send} loading={sending} data-testid="campaign-send"><Send className="h-4 w-4" /> Send campaign</Button>
            </div>
          </div>

          {/* History */}
          <p className="mb-2 mt-6 text-sm font-bold text-foreground">Sent campaigns</p>
          {campaigns.length === 0 ? <p className="py-4 text-sm text-muted-foreground">No campaigns sent yet.</p> : (
            <div className="space-y-2">
              {campaigns.map((c) => (
                <div key={c.id} className="rounded-xl border border-border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate font-semibold text-foreground">{c.subject}</p>
                    <span className="shrink-0 rounded-full bg-accent px-2 py-0.5 text-xs font-semibold text-brand-700 dark:text-brand-400">{c.recipient_count} sent</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{c.segment} · {fmtDateTime(c.created_at)} · by {c.sent_by}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Transactional notifications */}
        <div className="rounded-2xl border border-border bg-card p-5">
          <p className="mb-1 flex items-center gap-2 text-sm font-bold text-foreground"><Mail className="h-4 w-4 text-brand-600" /> Trip email notifications</p>
          <p className="mb-3 text-xs text-muted-foreground">Toggle or edit the automatic emails your app sends.</p>
          <div className="space-y-2">
            {notifs.map((n) => (
              <div key={n.event} data-testid={`notif-${n.event}`} className="rounded-xl border border-border p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-foreground">{n.label}</p>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setEditing(n)} data-testid={`notif-${n.event}-edit`} className="text-muted-foreground hover:text-brand-600"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => toggleNotif(n.event, !n.enabled)} data-testid={`notif-${n.event}-toggle`}
                      className={cn("relative h-6 w-11 rounded-full transition-colors", n.enabled ? "bg-brand-600" : "bg-secondary")}>
                      <span className={cn("absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all", n.enabled ? "left-5" : "left-0.5")} />
                    </button>
                  </div>
                </div>
                <p className="mt-1 truncate text-xs text-muted-foreground">{n.subject}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <NotifEditModal notif={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}

function NotifEditModal({ notif, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (notif) setForm({ subject: notif.subject || "", title: notif.title || "", body: notif.body || "", enabled: notif.enabled });
  }, [notif]);
  if (!notif) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try {
      await api(`/admin/notifications/${notif.event}`, { method: "PUT", body: {
        subject: form.subject, title: form.title, body: form.body,
      } });
      toast.success("Template saved");
      onSaved?.();
    } catch (e) { toast.error(e?.message || "Failed"); }
    finally { setSaving(false); }
  };
  const field = "w-full rounded-xl border border-border bg-secondary px-3 py-2.5 text-sm text-foreground outline-none focus:border-brand-500";
  return (
    <Modal open={!!notif} onClose={onClose} title={`Edit: ${notif.label}`} subtitle="Placeholders like {name}, {fare}, {code} are replaced automatically." size="xl"
      footer={<Button onClick={save} loading={saving} data-testid="notif-save" className="w-full">Save template</Button>}>
      <div className="space-y-3">
        <label className="block"><span className="mb-1 block text-xs text-muted-foreground">Subject</span>
          <input value={form.subject ?? ""} onChange={set("subject")} className={field} data-testid="notif-edit-subject" /></label>
        <label className="block"><span className="mb-1 block text-xs text-muted-foreground">Heading</span>
          <input value={form.title ?? ""} onChange={set("title")} className={field} data-testid="notif-edit-title" /></label>
        <label className="block"><span className="mb-1 block text-xs text-muted-foreground">Body (HTML)</span>
          <textarea value={form.body ?? ""} onChange={set("body")} rows={8} className={field} data-testid="notif-edit-body" /></label>
      </div>
    </Modal>
  );
}

/* ---------------- Live ---------------- */

function Live() {
  const [live, setLive] = useState(null);
  useEffect(() => {
    const load = () => api("/admin/live").then((r) => setLive(r.live || [])).catch(() => setLive([]));
    load();
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, []);
  if (!live) return <Spinner className="h-7 w-7 text-brand-600" />;
  return (
    <div>
      <h1 className="mb-5 flex items-center gap-2 text-2xl font-extrabold text-foreground"><Radio className="h-6 w-6 text-brand-600" /> Live rides</h1>
      {live.length === 0 ? <EmptyState icon={Radio} title="No active rides" subtitle="Live trips will appear here in real time." /> : (
        <div className="space-y-2">
          {live.map((r) => (
            <div key={r.id} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4">
              <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-success" />
              <div className="min-w-0 flex-1">
                <div className="mb-1"><StatusBadge status={r.status} /></div>
                <p className="truncate text-sm text-foreground">{r.pickup.label} → {r.destination.label}</p>
                <p className="text-xs text-muted-foreground">{r.customer_name} · {r.driver_name}</p>
              </div>
              <span className="font-num font-bold text-foreground">{money(r.fare)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Support ---------------- */

function Support() {
  const [convos, setConvos] = useState(null);
  const [active, setActive] = useState(null);
  const [text, setText] = useState("");
  const load = () => api("/admin/conversations").then((r) => {
    setConvos(r.conversations || []);
    setActive((a) => a ? (r.conversations || []).find((c) => c.ride_id === a.ride_id) || a : a);
  }).catch(() => setConvos([]));
  useEffect(() => { load(); const iv = setInterval(load, 4000); return () => clearInterval(iv); }, []);

  const reply = async () => {
    const t = text.trim();
    if (!t || !active) return;
    setText("");
    try { await api(`/admin/conversations/${active.ride_id}/message`, { method: "POST", body: { text: t } }); load(); }
    catch (e) { toast.error(e?.message || "Failed to send"); }
  };

  if (!convos) return <Spinner className="h-7 w-7 text-brand-600" />;
  return (
    <div>
      <h1 className="mb-5 text-2xl font-extrabold text-foreground">Support & conversations</h1>
      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <div className="space-y-2">
          {convos.length === 0 && <EmptyState icon={Headphones} title="No conversations" />}
          {convos.map((c) => (
            <button key={c.ride_id} onClick={() => setActive(c)} data-testid={`admin-convo-${c.ride_id}`}
              className={cn("w-full rounded-2xl border p-3 text-left", active?.ride_id === c.ride_id ? "border-brand-500 bg-accent" : "border-border bg-card")}>
              <p className="truncate font-bold text-foreground">{c.customer_name} {c.is_support && "· Support"}</p>
              <p className="truncate text-xs text-muted-foreground">{c.route}</p>
              <p className="truncate text-xs text-muted-foreground/80">{c.messages?.[c.messages.length - 1]?.text}</p>
            </button>
          ))}
        </div>
        <div className="flex min-h-[400px] flex-col rounded-2xl border border-border bg-card">
          {!active ? (
            <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">Select a conversation</div>
          ) : (
            <>
              <div className="border-b border-border p-4">
                <p className="font-bold text-foreground">{active.customer_name}</p>
                <p className="text-xs text-muted-foreground">{active.route}</p>
              </div>
              <div className="flex-1 space-y-2 overflow-y-auto p-4">
                {(active.messages || []).map((m) => {
                  const mine = m.sender_role === "support" || m.sender_role === "admin";
                  return (
                    <div key={m.id} className={cn("flex", mine ? "justify-end" : "justify-start")}>
                      <div className={cn("max-w-[75%] rounded-2xl px-3 py-2 text-sm", mine ? "bg-brand-600 text-white" : "bg-secondary text-foreground")}>
                        {!mine && <p className="mb-0.5 text-[11px] font-semibold opacity-70">{m.sender_name}</p>}
                        {m.text}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 border-t border-border p-3">
                <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && reply()}
                  placeholder="Reply as Getaride Support…" data-testid="admin-reply-input"
                  className="h-11 flex-1 rounded-full border border-border bg-secondary px-4 text-sm text-foreground outline-none focus:border-brand-500" />
                <button onClick={reply} data-testid="admin-reply-send" className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-600 text-white"><Send className="h-5 w-5" /></button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
